// mindmetal-app/web/src/components/employee/ApiRequestBuilder.tsx
import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button, Input, Select } from '@personic/shared'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface Header {
  key: string
  value: string
}

interface ApiRequestBuilderProps {
  method: HttpMethod
  onMethodChange: (method: HttpMethod) => void
  endpoint: string
  onEndpointChange: (endpoint: string) => void
  headers: Header[]
  onHeadersChange: (headers: Header[]) => void
  body: string
  onBodyChange: (body: string) => void
  onSend: () => void
  isLoading: boolean
  endpoints?: { value: string; label: string }[]
}

const METHOD_OPTIONS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
]

const methodColors: Record<HttpMethod, string> = {
  GET: 'text-emerald-600 bg-emerald-50',
  POST: 'text-blue-600 bg-blue-50',
  PUT: 'text-amber-600 bg-amber-50',
  PATCH: 'text-purple-600 bg-purple-50',
  DELETE: 'text-red-600 bg-red-50',
}

export function ApiRequestBuilder({
  method,
  onMethodChange,
  endpoint,
  onEndpointChange,
  headers,
  onHeadersChange,
  body,
  onBodyChange,
  onSend,
  isLoading,
  endpoints,
}: ApiRequestBuilderProps) {
  const [showCustomEndpoint, setShowCustomEndpoint] = useState(!endpoints?.length)

  const addHeader = () => {
    onHeadersChange([...headers, { key: '', value: '' }])
  }

  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    const newHeaders = [...headers]
    newHeaders[index][field] = value
    onHeadersChange(newHeaders)
  }

  const removeHeader = (index: number) => {
    onHeadersChange(headers.filter((_, i) => i !== index))
  }

  const formatBody = () => {
    try {
      const parsed = JSON.parse(body)
      onBodyChange(JSON.stringify(parsed, null, 2))
    } catch {
      // Invalid JSON, leave as is
    }
  }

  return (
    <div className="space-y-4">
      {/* Method & Endpoint */}
      <div className="flex gap-2">
        <div className="w-32">
          <Select
            options={METHOD_OPTIONS}
            value={method}
            onChange={(e) => onMethodChange(e.target.value as HttpMethod)}
            className={`font-mono font-semibold ${methodColors[method]}`}
          />
        </div>
        <div className="flex-1">
          {endpoints?.length && !showCustomEndpoint ? (
            <div className="flex gap-2">
              <div className="flex-1">
                <Select
                  options={[{ value: '', label: 'Select endpoint...' }, ...endpoints]}
                  value={endpoint}
                  onChange={(e) => onEndpointChange(e.target.value)}
                  className="font-mono"
                />
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCustomEndpoint(true)}
              >
                Custom
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  value={endpoint}
                  onChange={(e) => onEndpointChange(e.target.value)}
                  placeholder="/api/v3/..."
                  className="font-mono"
                />
              </div>
              {endpoints?.length && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCustomEndpoint(false)}
                >
                  Presets
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Headers */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-midnight-700">Headers</label>
          <button
            onClick={addHeader}
            className="flex items-center gap-1 text-sm text-copper-600 hover:text-copper-700"
          >
            <Plus className="w-4 h-4" /> Add Header
          </button>
        </div>
        <div className="space-y-2">
          {headers.map((header, index) => (
            <div key={index} className="flex gap-2 items-center">
              <Input
                value={header.key}
                onChange={(e) => updateHeader(index, 'key', e.target.value)}
                placeholder="Header name"
                className="font-mono text-sm"
              />
              <Input
                value={header.value}
                onChange={(e) => updateHeader(index, 'value', e.target.value)}
                placeholder="Value"
                className="font-mono text-sm"
              />
              <button
                onClick={() => removeHeader(index)}
                className="p-2 text-warm-400 hover:text-red-500 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {headers.length === 0 && (
            <p className="text-sm text-warm-400 italic">No custom headers</p>
          )}
        </div>
      </div>

      {/* Body */}
      {method !== 'GET' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-midnight-700">Body (JSON)</label>
            <button
              onClick={formatBody}
              className="text-sm text-copper-600 hover:text-copper-700"
            >
              Format
            </button>
          </div>
          <textarea
            value={body}
            onChange={(e) => onBodyChange(e.target.value)}
            placeholder='{"key": "value"}'
            rows={8}
            className="w-full px-4 py-3 border border-warm-300 rounded-xl bg-midnight-900 text-warm-100 font-mono text-sm placeholder:text-warm-600 focus:ring-2 focus:ring-copper-400 focus:border-copper-400 outline-none transition-all"
          />
        </div>
      )}

      {/* Send Button */}
      <Button onClick={onSend} isLoading={isLoading} fullWidth>
        Send Request
      </Button>
    </div>
  )
}
